import { parse, isValidNumber } from 'libphonenumber-js';
import moment from 'moment';
import memoize from 'lodash/memoize';
import isURL from 'validator/lib/isURL';

import { DateTimeFormats, DateFormats } from './DateInput/constants';


export const required =
  memoize(
    (message = (() => 'Fältet måste fyllas i.')) =>
      value =>
        ((value === undefined
        || value === null
        || value === ''
        || value.length === 0
        || value.size === 0) ?
          message(value) : undefined),
  );

export const gt =
  memoize(
    (reference, message = ((v, r) => `Värdet måste vara större än ${r}`)) =>
      value =>
        (parseFloat(value) > reference ? undefined : message(value, reference)),
  );

export const gtEq =
  memoize(
    (reference, message = ((v, r) => `Värdet måste vara minst ${r}`)) =>
      value =>
        (parseFloat(value) >= reference ? undefined : message(value, reference)),
  );

export const ltEq =
  memoize(
    (reference, message = ((v, r) => `Värdet måste vara mindre än ${r}.`)) =>
      value =>
        (parseFloat(value) <= reference ? undefined : message(value, reference)),
  );

export const ltEqNull =
  memoize(
    (reference, message = ((v, r) => `Värdet måste vara mindre än ${r}.`)) =>
      value =>
        (value === null || parseFloat(value) <= reference ? undefined : message(value, reference)),
  );

export const notApproxEq =
  memoize(
    (reference, delta = 0.02, message = ((v, r) => `Värdet borde vara ungefär ${r}.`)) =>
      value =>
        (Math.abs(reference - value) > delta ? message(value, reference) : undefined),
  );

export const email =
  memoize(
    (message = (v => `${v} är inte en giltig emailadress.`)) =>
      (value = '') => {
        const val = (value || '');

        if (val === '') {
          return undefined;
        }

        return val.match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        ) ? undefined : message(value);
      },
  );

export const numbersSpacesAndDashes =
  memoize(
    (message = (() => 'Fältet får bara innehålla siffror, bindestreck och mellanslag.')) =>
      (value = '') =>
        ((value || '').match(/[^\d-\s]/g) ? message() : undefined),
  );

export const numbersSpacesCommasAndDashes =
  memoize(
    (message = (() => 'Fältet får bara innehålla siffror, bindestreck, kommatecken och mellanslag.')) =>
      (value = '') =>
        ((value || '').match(/[^\d-,\s]/g) ? message() : undefined),
  );

export const url =
  memoize(
    (message = (() => 'Fältet måste vara en giltig hemsideaddress.')) =>
      (value = '') => {
        if (!value) {
          return undefined;
        }

        return (isURL(value) ? undefined : message());
      },
  );

export const phoneNumber =
  memoize(
    (message = (() => 'Telefonnumret måste vara giltigt.')) =>
      (value = '') => {
        if (!value) {
          return undefined;
        }
        return isValidNumber(parse(value)) ? undefined : message();
      },
  );

export const minLength =
  memoize(
    (reference, message = ((v, r) => `Fältet måste bestå av minst ${r} tecken`)) =>
      (value) => {
        if (!value) {
          return undefined;
        }
        return (value.length >= reference) ? undefined : message(value, reference);
      },
  );

export const maxLength =
  memoize(
    (reference, message = ((v, r) => `Fältet får max bestå av ${r} tecken`)) =>
      (value) => {
        if (!value) {
          return undefined;
        }
        return (value.length <= reference) ? undefined : message(value, reference);
      },
  );

export const exactLength =
  memoize(
    (reference, message = ((v, r) => `Fältet ska bestå av ${r} tecken`)) =>
      (value) => {
        if (!value) {
          return undefined;
        }
        return (value.length === reference) ? undefined : message(value, reference);
      },
  );

export const dateTime =
  memoize(
    (message = (() => 'Datumet måste vara giltigt')) =>
      (value = '') => {
        if (!value) {
          return undefined;
        }

        return moment(value, DateTimeFormats, true).isValid() ? undefined : message();
      },
  );

export const date =
  memoize(
    (message = (() => 'Datumet måste vara giltigt')) =>
      (value = '') => {
        if (!value) {
          return undefined;
        }

        return moment(value, DateFormats, true).isValid() ? undefined : message();
      },
  );

export const gtDate =
  memoize(
    (reference, message = (() => `Datumet måste vara efter ${moment(reference).format('YYYY-MM-DD')}`)) =>
      (value = '') => {
        if (!value) {
          return undefined;
        }
        const valDate = moment(value, DateFormats, true);

        return valDate.diff(reference) >= 0 ? undefined : message();
      },
  );

export const ltDate =
  memoize(
    (reference, message = (() => `Datumet måste vara före ${moment(reference).format('YYYY-MM-DD')}`)) =>
      (value = '') => {
        if (!value) {
          return undefined;
        }
        const valDate = moment(value, DateFormats, true);

        return valDate.diff(reference) <= 0 ? undefined : message();
      },
  );

  export const isDomain =
  memoize(
    (message = (() => 'Domänen är inte giltig.')) =>
      (value = '') =>
        (!(value).match(/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/g) ? message() : undefined),
  );

/**
 * Arbitrary validation rule. If the passed conditionFunc returns true,
 * data is considered invalid. Undefined is considered valid.
 * @param conditionFunc
 * @param message
 * @returns {function()}
 */
export const condition =
memoize(
  (conditionFunc, message = (() => 'Värdet är inte giltigt.')) =>
    (values, allValues, props) =>
      (conditionFunc(values, allValues, props) ? message() : undefined),
);
